import Alert from '@mui/material/Alert'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import KNLoader from 'components/KNLoader/KNLoader'
import { useTripsView } from 'context/TripsViewContext'
import { analyticsEvent, analyticsPageView } from 'global/helpers/analytics'
import { getSearchCondition, getValueCondition } from 'global/helpers/filtering'
import { combineQueries } from 'global/helpers/queries'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import { sleep } from 'global/helpers/sleep'
import TheHulk from 'modules/TheHulk/TheHulk'
import { type ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { driversQueryOptions } from 'screens/DriverManager/DriverManager.service'
import { vehiclesQueryOptions } from 'screens/VehicleManager/VehicleManager.service'
import PredefinedFilters from './PredefinedFilters'
import { isTripMatchingDateFilter, processBranches, processDriversAndVehicles, processTrackingInformation } from './TripDashboard.helpers'
import {
  sendTripAcceptance,
  tripCountersQueryOptions,
  TRIPS_QUERY_KEY,
  tripsQueryOptions,
  tripTrackingInformationQueryOptions,
} from './TripDashboard.service'
import type { TripData } from './TripDashboard.types'
import TripListFilters from './TripListFilters'
import TripTable from './TripTable'

const combineTripsQueries = combineQueries(['vehicles', 'drivers', 'counters', 'trips'])

const TripDashboard = (): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { filters, predefinedFilter, restoreScrollPosition } = useTripsView()
  const tripsQueries = useQueries({
    queries: [
      vehiclesQueryOptions,
      driversQueryOptions,
      tripCountersQueryOptions,
      tripsQueryOptions(predefinedFilter),
    ],
    combine: combineTripsQueries,
  })
  const trackingInformationQuery = useQuery(tripTrackingInformationQueryOptions())
  const tripsData = useMemo(() => {
    if (!tripsQueries.isSuccess) {
      return []
    }
    return tripsQueries.data.trips.map((trip) =>
      processTrackingInformation(
        processDriversAndVehicles(trip, tripsQueries.data.vehicles, tripsQueries.data.drivers),
        trackingInformationQuery.data ?? [],
      ),
    )
  }, [tripsQueries, trackingInformationQuery.data])
  const branchesData = useMemo(() => processBranches(tripsData), [tripsData])
  const tripAcceptance = useMemo(() => tripsData.some((trip) => ['PENDING', 'ACCEPTED', 'DECLINED'].includes(trip.tripAcceptanceStatus)), [tripsData])

  const refreshTrips = useCallback(async (): Promise<void> => {
    await sleep(2000)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [TRIPS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: tripCountersQueryOptions.queryKey }),
    ])
  }, [queryClient])
  const tripAcceptanceMutation = useMutation({
    mutationFn: ({ tripCid, status }: { tripCid: string, status: 'ACCEPTED' | 'DECLINED' }) =>
      sendTripAcceptance(tripCid, { status }),
    onSuccess: refreshTrips,
  })

  useEffect(() => {
    restoreScrollPosition()
    analyticsPageView('polestar/cs/trips_list')
  }, [])

  const handleOnChange = useCallback(async (updatedTrip: TripData, action?: string): Promise<void> => {
    if (action === 'trip_accepted') {
      await tripAcceptanceMutation.mutateAsync({ tripCid: updatedTrip.entityId, status: 'ACCEPTED' })
      analyticsEvent('polestar_cs_trip_acceptance', ['accepted'])
    } else if (action === 'trip_declined') {
      await tripAcceptanceMutation.mutateAsync({ tripCid: updatedTrip.entityId, status: 'DECLINED' })
      analyticsEvent('polestar_cs_trip_acceptance', ['declined'])
    } else {
      await refreshTrips()
    }
  }, [refreshTrips, tripAcceptanceMutation])

  const filteredTripsData = useMemo(() => tripsData.filter((trip) => {
    const keywordsCondition = getSearchCondition([
      trip.voyageNumber,
      trip.pickUpPlace,
      trip.countryCodePickUpPlace,
      trip.carrierInstructions,
      trip.countryPickUpPlace,
      trip.deliveryPlace,
      trip.countryCodeDeliveryPlace,
      trip.countryDeliveryPlace,
      trip.assignedDriver?.name,
      trip.assignedTruck?.licensePlate,
      trip.assignedTrailer?.licensePlate,
      ...trip.shipmentNumbers,
      ...trip.customerReferences,
    ], filters.keywords)

    const statusCondition = getValueCondition<string>(trip.status, filters.status)

    const dateCondition = isTripMatchingDateFilter(trip, filters)

    const branchesCondition = getValueCondition<string>(trip.logicalSender, filters.branches)

    return [keywordsCondition, statusCondition, dateCondition, branchesCondition].every(
      (condition: boolean) => condition,
    )
  }), [tripsData, filters])

  return (
    <Container maxWidth="xxl">
      <TheHulk placement="user_area" />
      {tripsQueries.isPending && (
        <KNLoader>
          <Typography>{t('screens.cs.trip_dashboard.loading')}</Typography>
        </KNLoader>
      )}
      {tripsQueries.isError && (
        <Alert severity="error">{getRequestErrorMessage(tripsQueries.error)}</Alert>
      )}
      {tripsQueries.isSuccess && (
        <>
          <PredefinedFilters
            counters={tripsQueries.data.counters}
            sx={{
              mb: 2,
            }}
          />
          <Paper sx={{ padding: 2 }}>
            <Stack direction="column" spacing={1} mb={1}>
              <TripListFilters branches={branchesData} />
              <Typography variant="h6">
                {t('screens.cs.trip_dashboard.trips_count', { count: filteredTripsData.length })}
              </Typography>
            </Stack>
            <TripTable trips={filteredTripsData} tripAcceptance={tripAcceptance} onChange={handleOnChange} />
          </Paper>
        </>
      )}
    </Container>
  )
}

export default TripDashboard
